import React, { useState } from 'react';
import { Modal, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import useFetchAllLocations from '../../Hooks/useFetchAllLocations';
import { Location, City } from '../../types/locationType';
import { IoChevronBack, IoSearch } from "react-icons/io5";
import { FaMagnifyingGlassLocation } from "react-icons/fa6";
import LocationsSkeleton from './LocationsSkeleton';

interface LocationSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const LocationSelectionModal: React.FC<LocationSelectionModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { states, cities, loading } = useFetchAllLocations();
    const [selectedState, setSelectedState] = useState<Location | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Handle state selection
    const handleStateSelect = (state: Location) => {
        setSelectedState(state);
        setSearchTerm('');
    };

    // Handle city selection
    const handleCitySelect = (state: Location, city: City) => {
        // Navigate to the search page filtered to this city, same workflow as category/subcategory selection
        navigate(`/search/location/${state.id}/${city.id}`);

        // Create and dispatch the custom event so the dropdown label updates
        const event = new CustomEvent('locationSelected', {
            detail: {
                stateId: state.id,
                cityId: city.id,
                stateName: state.name,
                cityName: city.name
            }
        });
        window.dispatchEvent(event);

        // Close the modal after selection
        onClose();
        setSelectedState(null);
    };

    // Go back to states list
    const handleBackToStates = () => {
        setSelectedState(null);
        setSearchTerm('');
    };

    // Filter states or cities based on search term, ordered by number of ads
    const filteredStates = states
        .filter(state => state.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => (b.adCount || 0) - (a.adCount || 0));

    const filteredCities = selectedState ? cities
        .filter(city =>
            city.state_id === selectedState.id &&
            city.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => (b.adCount || 0) - (a.adCount || 0)) : [];

    return (
        <Modal
            title={null}
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={600}
            centered
            styles={{ body: { padding: '0', maxHeight: '80vh', overflow: 'hidden' } }}
            className="location-selection-modal"
        >
            <div className="p-4 bg-gradient-to-r from-accent-default via-accent-default to-accent-secondary text-white relative">
                <div className="flex items-center">
                    {selectedState && (
                        <Button
                            type="text"
                            icon={<IoChevronBack className="text-white" />}
                            onClick={handleBackToStates}
                            className="mr-2 flex items-center justify-center border-none"
                            style={{ color: 'white', background: 'rgba(255,255,255,0.1)' }}
                        />
                    )}
                    <h2 className="text-xl font-semibold m-0 flex-1">
                        {selectedState ? `Villes dans ${selectedState.name}` : "Sélectionner une région"}
                    </h2>
                </div>

                <div className="mt-3 bg-white/20 rounded-lg flex items-center px-3 py-2">
                    <IoSearch className="text-white/80 mr-2" size={20} />
                    <input
                        type="text"
                        placeholder={selectedState ? "Rechercher une ville..." : "Rechercher une région..."}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 bg-transparent border-none text-white placeholder-white/70 outline-none"
                    />
                </div>
            </div>

            <div className="overflow-y-auto p-3" style={{ maxHeight: 'calc(80vh - 130px)' }}>
                {loading ? (
                    <LocationsSkeleton />
                ) : selectedState ? (
                    // Display cities for selected state
                    filteredCities.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2">
                            {filteredCities.map(city => (
                                <div
                                    key={city.id}
                                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                                    onClick={() => handleCitySelect(selectedState, city)}
                                >
                                    <div className="flex items-center">
                                        <span className="text-primary-default text-lg mr-2">🏙️</span>
                                        <span className="font-medium text-gray-700">{city.name}</span>
                                    </div>
                                    {city.adCount !== undefined && (
                                        <span className="bg-primary-tertiary text-primary-default text-xs px-2 py-1 rounded-full">
                                            {city.adCount} articles
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            Aucune ville trouvée pour cette recherche
                        </div>
                    )
                ) : (
                    // Display states list
                    filteredStates.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2">
                            {filteredStates.map(state => (
                                <div
                                    key={state.id}
                                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                                    onClick={() => handleStateSelect(state)}
                                >
                                    <div className="flex items-center">
                                        <span className="text-primary-default text-lg mr-2"><FaMagnifyingGlassLocation /></span>
                                        <span className="font-medium text-gray-700">{state.name}</span>
                                    </div>
                                    {state.adCount !== undefined && (
                                        <span className="bg-primary-tertiary text-primary-default text-xs px-2 py-1 rounded-full">
                                            {state.adCount} articles
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            Aucune région trouvée pour cette recherche
                        </div>
                    )
                )}
            </div>
        </Modal>
    );
};

export default LocationSelectionModal;
